import React, { useEffect, useState } from 'react';

import { Box, FormControl, Grid, MenuItem } from '@material-ui/core';
import moment from 'moment';

import { CustomDatePicker, CustomInputSelect, InputField } from 'src/components';
import { CustomLabel } from 'src/pages/signup/components/types';
import { ISearchUser } from 'src/services/users';
import { capitalizeFirstLetter, shallowEqual } from 'src/utils/common';

import {
  verificationStatus,
  CustomButtonStyled,
  eligibleStatus,
  accountEligibilityTypes,
  defaultSearchEligibleUser,
} from '../types';

const LEAST_CHAR_FOR_SEARCH = 3;

interface ISearchUserView {
  _onSearch: (search: ISearchUser) => void;
  _onReset: () => void;
  loading: boolean;
  searchData: ISearchUser;
}

const SearchEligibleUser = ({ searchData, loading, _onSearch, _onReset }: ISearchUserView) => {
  const [search, setSearch] = useState<ISearchUser>(defaultSearchEligibleUser);

  function _handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch({ ...search, [e.target.name]: e.target.value === 'all' ? '' : e.target.value });
  }

  const handleFromDateChange = (date: Date | null) => {
    if (date) {
      setSearch({ ...search, fromDate: date });
    }
  };
  const handleToDateChange = (date: Date | null) => {
    if (date) {
      setSearch({ ...search, toDate: date });
    }
  };

  const reset = () => {
    _onReset();
    setSearch(defaultSearchEligibleUser);
  };

  useEffect(() => {
    if (!shallowEqual(search, searchData)) {
      setSearch(searchData);
    }
  }, []);

  return (
    <Box mb={4}>
      <Grid container justifyContent="space-between">
        <Grid item xs={8}>
          <Box display="flex" justifyContent="space-between">
            <Box flex={1} pr={1}>
              <InputField
                title="User information"
                placeholder="Enter at least 3 characters"
                name="search"
                value={search.search}
                onChange={_handleChange}
                margin="dense"
                fullWidth
                notice
                noticeTitle={'You can search by NSAA ID, Name, Email Address or Phone number'}
                noticeYellow
                InputProps={{
                  inputProps: {
                    maxLength: 50,
                  },
                }}
                error={search.search !== '' && search.search.length < LEAST_CHAR_FOR_SEARCH}
                errorMessage="Enter at least 3 characters"
              />
            </Box>
            <Box flex={1} pl={1}>
              <CustomInputSelect
                title="Eligible Status"
                name="eligibleStatus"
                value={search.eligibleStatus === '' ? 'all' : search.eligibleStatus}
                margin="dense"
                fullWidth
                notRequired
                onChange={_handleChange}
              >
                <MenuItem key="all" value="all">
                  All
                </MenuItem>
                {eligibleStatus.map((status) => (
                  <MenuItem key={status.key} value={status.key}>
                    {capitalizeFirstLetter(status.value)}
                  </MenuItem>
                ))}
              </CustomInputSelect>
            </Box>
          </Box>

          <Box display="flex" justifyContent="space-between">
            <Box flex={1} pr={1}>
              <CustomInputSelect
                title="Account type"
                name="accountType"
                value={search.accountType === '' ? 'all' : search.accountType}
                margin="dense"
                fullWidth
                notRequired
                onChange={_handleChange}
              >
                <MenuItem key="all" value="all">
                  All
                </MenuItem>
                {accountEligibilityTypes.map((accountType) => (
                  <MenuItem key={accountType.key} value={accountType.key}>
                    {capitalizeFirstLetter(accountType.value)}
                  </MenuItem>
                ))}
              </CustomInputSelect>
            </Box>
            <Box flex={1} pl={1}>
              <CustomInputSelect
                title="Verification status"
                name="verificationStatus"
                value={search.verificationStatus === '' ? 'all' : search.verificationStatus}
                margin="dense"
                fullWidth
                notRequired
                onChange={_handleChange}
              >
                <MenuItem key="all" value="all">
                  All
                </MenuItem>
                {verificationStatus.map((status) => (
                  <MenuItem key={status.key} value={status.key}>
                    {capitalizeFirstLetter(status.value)}
                  </MenuItem>
                ))}
              </CustomInputSelect>
            </Box>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Box flex={1} pr={2} display="flex" mt={3}>
              <FormControl fullWidth>
                <CustomLabel>From Date</CustomLabel>
                <CustomDatePicker
                  onChange={handleFromDateChange}
                  value={search.fromDate ? new Date(search.fromDate) : null}
                  name="fromDate"
                  maxDate={search.toDate ? moment(search.toDate).subtract(0, 'years') : moment().subtract(0, 'years')}
                />
              </FormControl>
            </Box>
            <Box flex={1} display="flex" mt={3}>
              <FormControl fullWidth>
                <CustomLabel>To Date</CustomLabel>
                <CustomDatePicker
                  onChange={handleToDateChange}
                  value={search.toDate ? new Date(search.toDate) : null}
                  name="toDate"
                  minDate={moment(search?.fromDate).subtract(0, 'years')}
                />
              </FormControl>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box display="flex" justifyContent="flex-end" mt={6}>
            {!shallowEqual(searchData, defaultSearchEligibleUser) && (
              <Box display="flex">
                <CustomButtonStyled disabled={loading} outlineButton onClick={reset}>
                  Reset
                </CustomButtonStyled>
              </Box>
            )}
            <Box display="flex">
              <CustomButtonStyled
                disabled={loading || (search.search !== '' && search.search.length < LEAST_CHAR_FOR_SEARCH)}
                onClick={_onSearch(search)}
              >
                Search
              </CustomButtonStyled>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchEligibleUser;
